import React, { useRef, useState, useEffect } from 'react'

const CoffeeWebVideo = ({ isLoading }) => {
  const videoRef = useRef(null)
  const timeoutRef = useRef(null)
  const isPlayingRef = useRef(false)

  const [isPlaying, setIsPlaying] = useState(true)
  const [showCenterButton, setShowCenterButton] = useState(false)

  useEffect(() => {
    if (!showCenterButton) return

    const timeout = setTimeout(() => {
      setShowCenterButton(false)
    }, 1500)

    return () => clearTimeout(timeout)
  }, [showCenterButton])

  useEffect(() => {
    const video = videoRef.current

    if (!video) return

    video.loop = true
    video.muted = false
    video.volume = 1.0

    let isCancelled = false

    const handleVideo = async () => {
      if (!isLoading && video.paused && !isPlayingRef.current) {
        isPlayingRef.current = true
        try {
          // Wait for video to be ready
          if (video.readyState < 3) {
            await new Promise((resolve) => {
              const handleCanPlay = () => {
                video.removeEventListener('canplay', handleCanPlay)
                resolve()
              }

              video.addEventListener('canplay', handleCanPlay)
            })
          }

          if (isCancelled) return

          await video.play()
          if (isCancelled) return
          setIsPlaying(true)
        } catch (err) {
          console.warn('Autoplay with sound failed:', err)
          if (isCancelled) return
          video.muted = true
          try {
            await video.play()
            if (isCancelled) return
            setIsPlaying(true)
          } catch (mutedErr) {
            console.warn('Autoplay even with muted failed:', mutedErr)
            setIsPlaying(false)
          }
        } finally {
          isPlayingRef.current = false
        }

        if (!isCancelled) {
          setShowCenterButton(true)
          timeoutRef.current = setTimeout(() => setShowCenterButton(false), 1500)
        }
      } else if (isLoading && !video.paused) {
        video.pause()
        setIsPlaying(false)
        isPlayingRef.current = false
      }
    }

    // Add a small delay to prevent rapid state changes
    const timeoutId = setTimeout(handleVideo, 100)

    return () => {
      isCancelled = true
      clearTimeout(timeoutId)
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [isLoading])

  const togglePlayPause = async (e) => {
    if (e.target !== videoRef.current) return

    const video = videoRef.current

    if (!video || isPlayingRef.current) return

    if (video.paused) {
      isPlayingRef.current = true
      try {
        await video.play()
        setIsPlaying(true)
      } catch (err) {
        console.warn('Play failed:', err)
        // If play fails, try with muted
        try {
          video.muted = true
          await video.play()
          setIsPlaying(true)
        } catch (mutedErr) {
          console.warn('Muted play also failed:', mutedErr)
          setIsPlaying(false)
        }
      } finally {
        isPlayingRef.current = false
      }
    } else {
      video.pause()
      setIsPlaying(false)
    }

    setShowCenterButton(true)
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => setShowCenterButton(false), 1000)
  }

  const onPlay = () => setIsPlaying(true)
  const onPause = () => setIsPlaying(false)

  return (
    <div className="video-container" onClick={togglePlayPause}>
      <video ref={videoRef} className="styled-video" onPlay={onPlay} onPause={onPause} controls loop playsInline onClick={togglePlayPause}>
        <source src={process.env.REACT_APP_ABOUT_COFFEE_WEB_VIDEO_LINK} type="video/mp4" />
      </video>

      {showCenterButton && <div className="center-play-button">{isPlaying ? <span>⏸</span> : <span>►</span>}</div>}
    </div>
  )
}

export default CoffeeWebVideo
